import { useCallback, useEffect, useMemo, useState } from 'react';

type FullscreenDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => Promise<void> | void;
  msFullscreenElement?: Element | null;
  msExitFullscreen?: () => Promise<void> | void;
};

type FullscreenEl = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

/** True when the browser is in Fullscreen API fullscreen (standard/vendor). */
const isApiFullscreen = () => {
  const doc = document as FullscreenDoc;
  return Boolean(
    doc.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement,
  );
};

const requestApiFullscreen = async () => {
  const el = document.documentElement as FullscreenEl;
  if (typeof el.requestFullscreen === 'function') {
    await el.requestFullscreen();
    return;
  }
  if (typeof el.webkitRequestFullscreen === 'function') {
    await el.webkitRequestFullscreen();
    return;
  }
  if (typeof el.msRequestFullscreen === 'function') {
    await el.msRequestFullscreen();
  }
};

const exitApiFullscreen = async () => {
  const doc = document as FullscreenDoc;
  if (typeof doc.exitFullscreen === 'function') {
    await doc.exitFullscreen();
    return;
  }
  if (typeof doc.webkitExitFullscreen === 'function') {
    await doc.webkitExitFullscreen();
    return;
  }
  if (typeof doc.msExitFullscreen === 'function') {
    await doc.msExitFullscreen();
  }
};

export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(() => isApiFullscreen());

  useEffect(() => {
    const update = (): void => {
      setIsFullscreen(isApiFullscreen());
    };
    document.addEventListener('fullscreenchange', update);
    return () => {
      document.removeEventListener('fullscreenchange', update);
    };
  }, []);

  const supported = useMemo(
    () => {
      if (typeof document === 'undefined') return false;
      const doc = document as FullscreenDoc;
      const el = document.documentElement as FullscreenEl;
      return Boolean(
        doc.fullscreenEnabled ||
          doc.webkitFullscreenEnabled ||
          typeof el.requestFullscreen === 'function' ||
          typeof el.webkitRequestFullscreen === 'function' ||
          typeof el.msRequestFullscreen === 'function',
      );
    },
    [],
  );

  const toggle = useCallback(async () => {
    if (!supported) return;
    try {
      if (isApiFullscreen()) {
        await exitApiFullscreen();
      } else {
        await requestApiFullscreen();
      }
    } catch (error) {
      console.warn('Fullscreen toggle failed', error);
    }
  }, [supported]);

  return { supported, isFullscreen, toggle };
}
